import {useRef, type ReactNode} from 'react';
import {Camera, ImagePlus} from 'lucide-react';
import {toast} from 'sonner';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {requestHeadingPermission} from '@/lib/photo-capture/heading';
import {
  galleryAccept,
  isImageFile,
  type PhotoPick,
  type PhotoSource,
} from '@/lib/photo-capture/image-input';

interface PhotoSourceMenuProps {
  /** The button that opens the menu. */
  children: ReactNode;
  /** Receives the picked file and which source produced it. */
  onPick: (pick: PhotoPick) => void;
  /** Menu alignment relative to the trigger. */
  align?: 'start' | 'center' | 'end';
  /**
   * Refuse to open. Set while an upload is in flight: picking a replacement
   * mid-upload cannot cancel the request, so the caller would abandon a photo
   * the server still stores.
   */
  disabled?: boolean;
}

/**
 * A "choose Camera or Gallery" menu wrapping the two file inputs.
 *
 * Splitting the choice across two taps is what makes the iOS compass work.
 * Motion & Orientation access must be requested from a user gesture, but it
 * cannot share the gesture that opens the camera — the native prompt consumes
 * the tap, leaving the subsequent `input.click()` ungestured so iOS silently
 * refuses to open the camera at all. Opening this menu is a gesture that opens
 * no camera, so the request rides it and the camera opens later on its own
 * separate tap. The user never sees a dedicated "enable compass" control.
 *
 * The request is made only on menu-open. Repeating it on the camera tap bought
 * nothing — opening the menu is already a gesture, so if that request did not
 * settle the grant, a second one on the next tap will not either.
 */
export function PhotoSourceMenu({
  children,
  onPick,
  align = 'start',
  disabled = false,
}: PhotoSourceMenuProps) {
  const cameraRef = useRef<HTMLInputElement>(null);
  const galleryRef = useRef<HTMLInputElement>(null);

  const handleOpenChange = (open: boolean) => {
    // Fire-and-forget: a denial just means the compass stamp has no bearing,
    // and the grant persists for the origin so this is a one-off in practice.
    if (open && !disabled) void requestHeadingPermission();
  };

  const handleFile = (source: PhotoSource) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    // The gallery input is unfiltered on Android (see `galleryAccept`), so the
    // file may be anything — validate rather than trusting `accept`.
    if (!isImageFile(file)) {
      toast.error('Please choose an image file (JPG, PNG, WebP or HEIC).');
      return;
    }
    onPick({file, source});
  };

  return (
    <>
      <DropdownMenu onOpenChange={handleOpenChange}>
        <DropdownMenuTrigger asChild disabled={disabled}>
          {children}
        </DropdownMenuTrigger>
        <DropdownMenuContent align={align} className="min-w-40">
          <DropdownMenuItem onSelect={() => cameraRef.current?.click()}>
            <Camera className="mr-2 size-4" />
            Camera
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => galleryRef.current?.click()}>
            <ImagePlus className="mr-2 size-4" />
            Gallery
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={handleFile('camera')}
      />
      <input
        ref={galleryRef}
        type="file"
        accept={galleryAccept()}
        className="hidden"
        onChange={handleFile('gallery')}
      />
    </>
  );
}
